import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useViewport } from "../../../helpers";
import clsx from "clsx";
import Paper from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import DndKit from "../../../components/dragNDrop/DndKit";
import ImageAndTitle from "../../../components/dragNDrop/draggableElements/ImageAndTitle";
import CustomTabPanel from "./CustomTabPanel";
import InfiniteContent from "./InfiniteContent";
import SearchContent from "./SearchContent";

const PREFIX = "DragAndDropList";

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  draggableList: `${PREFIX}-draggableList`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  sectionTitleStyle: `${PREFIX}-sectionTitleStyle`,
  subtitleDnd: `${PREFIX}-subtitleDnd`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
    margin: "1rem 0",
    "& > div > div > div": {
      overflow: "unset !important",
    },
  },

  [`& .${classes.draggableList}`]: {
    height: "150px",
    display: "flex",
    marginRight: "1rem",
    overflow: "auto",
    width: "50%",
    marginTop: "5.5rem",
    paddingTop: "0.5rem",
    backgroundColor: "#424242",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
      marginTop: "0",
      height: "115px",
    },
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.sectionTitle}`]: {
    textAlign: "center",
  },

  [`& .${classes.sectionTitleStyle}`]: {
    color: theme.palette.primary.main,
    marginBottom: "1rem",
  },

  [`& .${classes.subtitleDnd}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "1rem",
      position: "initial",
      top: "inherit",
      left: "inherit",
    },
    position: "relative",
    top: "2.5rem",
    left: "6rem",
    width: "150px",
    height: "30px",
  },
}));

function DragAndDropList({
  id,
  logoKey,
  subLogoKey,
  horizontal,
  baseList,
  setBaseList,
  ddList,
  setterDDList,
  sectionTitle,
  baseListTitle,
  dradOrderTitle,
  getMoreData,
  apiHasMore,
  searchApi,
  isAsync,
}) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { width } = useViewport();
  const { t } = useTranslation();

  const getNextElements = () => {
    if (!Array.isArray(baseList)) {
      getMoreData(
        Object.keys(baseList)[selectedTab],
        id === "expariences",
        id === "currentExpariences"
      );
    } else getMoreData();
  };

  const handleSaveOrderedArray = (newArr) => {
    setterDDList(newArr);
  };

  const handleRemoveElement = (elem) => {
    const tmp = [...ddList[id]];
    const indexToRemove = tmp.findIndex((item) => item.id === elem.id);
    tmp.splice(indexToRemove, 1);
    setterDDList(tmp);
  };

  const handleAddElement = (elem) => {
    setterDDList([...ddList[id], elem]);
  };

  return (
    <Paper
      elevation={3}
      style={{
        padding: "1rem",
        marginBottom: "2rem",
        backgroundColor: "#424242",
      }}
    >
      <StyledDiv>
        <Typography
          variant="h5"
          className={clsx(
            width >= 960 && classes.sectionTitle,
            classes.sectionTitleStyle
          )}
        >
          {sectionTitle}
        </Typography>
        <Typography variant="body1">{baseListTitle}</Typography>
        <div className={classes.mainContainer}>
          {!Array.isArray(baseList) ? (
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={selectedTab}
                  onChange={(e, index) => setSelectedTab(index)}
                >
                  {Object.keys(baseList).map((key, index) => (
                    <Tab
                      key={`panel-${id}-${index}`}
                      label={
                        key === "entities"
                          ? t("myExpariences.dmaAdminPage.entitiesPanelTitle")
                          : key
                      }
                      id={`panel-${id}-${index}`}
                      aria-controls={`tabpanel-${id}-${index}`}
                    />
                  ))}
                </Tabs>
              </Box>

              {Object.keys(baseList).map((key, index) => {
                return (
                  <CustomTabPanel
                    value={selectedTab}
                    index={index}
                    key={`custom-panel-${id}-${index + 1}`}
                  >
                    <SearchContent
                      isAsync={isAsync}
                      searchApi={async (name) =>
                        await searchApi[index](
                          name,
                          id === "promotedLogos",
                          id === "promotedVideos"
                        )
                      }
                      id={id}
                      baseOptions={baseList[key].data}
                      selectedList={ddList[id]}
                      setResults={(newArr) => setBaseList(newArr, key)}
                    />
                    <InfiniteContent
                      baseList={baseList[key].data}
                      getNextElements={getNextElements}
                      apiHasMore={apiHasMore[key].hasMore}
                      id={id}
                      ddList={ddList}
                      logoKey={logoKey}
                      subLogoKey={subLogoKey}
                      handleAddElement={handleAddElement}
                    />
                  </CustomTabPanel>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <SearchContent
                isAsync={isAsync}
                searchApi={(name) =>
                  searchApi(
                    name,
                    id === "promotedLogos",
                    id === "promotedVideos"
                  )
                }
                id={id}
                baseOptions={baseList}
                selectedList={ddList[id]}
                setResults={(newArr) => setBaseList(newArr)}
              />
              <InfiniteContent
                baseList={baseList}
                getNextElements={getNextElements}
                apiHasMore={apiHasMore}
                id={id}
                ddList={ddList}
                logoKey={logoKey}
                subLogoKey={subLogoKey}
                handleAddElement={handleAddElement}
              />
            </Box>
          )}
          <Typography variant="body1" className={classes.subtitleDnd}>
            {dradOrderTitle}
          </Typography>
          <Paper
            elevation={10}
            className={classes.draggableList}
            sx={{ marginBottom: "2rem" }}
          >
            <DndKit
              mode={horizontal ? "horizontal" : "vertical"}
              data={ddList[id]}
              reorderedArray={(newArray) => handleSaveOrderedArray(newArray)}
              renderItem={(elem, index) => (
                <ImageAndTitle
                  image={elem[logoKey] || elem[subLogoKey]}
                  title={elem.displayName || elem.name}
                  onRemove={() => handleRemoveElement(elem, index)}
                />
              )}
            />
          </Paper>
        </div>
      </StyledDiv>
    </Paper>
  );
}

export default DragAndDropList;
